import {
  forwardRef,
  ComponentPropsWithoutRef,
  ReactNode,
  FocusEventHandler,
  MouseEventHandler,
} from "react";
import clsx from "clsx";
import ErrorBoundary from "../../error-boundary";

import Link from "../../link";
import { Body2 } from "../../typography";

import styles from "./menu-item.module.scss";
import { types } from "../../link/Link";
import { FlexGrid } from "../../flex-grid";
import { ChevronDownOutline } from "@aurora/icons-react";

type Types = typeof types;

export type MenuItemProps = {
  /** Optional extra class(es) */
  className?: string;
  /** Nested level, default is 0 */
  nested?: number;
  /** The type of element (tag) to use */
  type?: keyof Types;
  /** The link for the menu item points to */
  to?: string;
  /** An icon to be displayed on the left side */
  iconLeft?: any;
  /** An icon to be displayed on the right side */
  iconRight?: any;
  /** The contents of the menu item */
  content?: string;
  /** The second line of text */
  line2?: string;
  /** The third line of text */
  line3?: string;
  /** Sets the iconRight to chevron and automaticlly rotates it on selected. Styles for states are also affected. Makes the `selected`-prop act as a `isOpen`-prop. */
  collapsible?: boolean;
  /** Used to indicates if a nested MenuItem is selected */
  selectedWithin?: boolean;
  /** Any nested sub menus */
  children?: ReactNode;
  /** Set to true to mark as selected item */
  selected?: boolean;
} & ComponentPropsWithoutRef<"button" | "a">;

const _MenuItem = forwardRef<HTMLLIElement, MenuItemProps>(
  (
    {
      className,
      type = "navLink",
      nested = 0,
      to,
      iconLeft,
      iconRight,
      content,
      line2,
      line3,
      children,
      selected,
      collapsible = false,
      selectedWithin,
      onFocus,
      onBlur,
      ...rest
    },
    ref
  ) => {
    const classes = [
      styles.menuItem,
      {
        [styles.selected]: selected,
        [styles.selectedWithin]: !(collapsible && selected) && selectedWithin,
        [styles.nested1]: nested === 1,
        [styles.nested2]: nested === 2,
        [styles.nested3]: nested === 3,
      },
      collapsible && styles.collapsible,
    ];

    const Tag = types[type];

    if (collapsible) {
      iconRight = (
        <ChevronDownOutline
          style={{
            rotate: selected ? "180deg" : undefined,
            transition: "rotate var(--transition-time)",
            marginLeft: "auto",
          }}
        />
      );
    }
    const block = (
      <div
        className={clsx(
          styles.content,
          (iconLeft || iconRight) && styles.hasIcon
        )}
      >
        {iconLeft}
        {line2 || line3 ? (
          <FlexGrid
            direction={FlexGrid.directions.vertical}
            className={styles.listItemText}
          >
            <div>{content}</div>
            {line2 !== undefined ? <Body2>{line2}</Body2> : undefined}
            {line3 !== undefined ? <Body2>{line3}</Body2> : undefined}
          </FlexGrid>
        ) : (
          <div>{content}</div>
        )}
        {iconRight}
      </div>
    );
    return (
      <li
        ref={ref}
        className={clsx(styles.resetList, className)}
        id={rest.id}
        {...(rest as any)}
      >
        <ErrorBoundary>
          {Tag === types.navLink || Tag === types.link || Tag === types.a ? (
            <Link
              type={type}
              className={clsx(classes)}
              to={to as string}
              style={{ paddingLeft: `${nested * 1}rem` }}
              noLinkStyling
              {...(rest as any)}
            >
              {block}
            </Link>
          ) : (
            <button
              type="button"
              className={clsx(classes)}
              onFocus={onFocus as FocusEventHandler<HTMLButtonElement>}
              onBlur={onBlur as FocusEventHandler<HTMLButtonElement>}
              onClick={(e: any) => {
                if (rest && rest.onClick) {
                  e.stopPropagation();
                  rest.onClick(e);
                }
              }}
              style={{ paddingLeft: `${nested * 1}rem` }}
            >
              {block}
            </button>
          )}
          {children}
        </ErrorBoundary>
      </li>
    );
  }
);

export type MenuItem = typeof _MenuItem & {
  types: RecordKeys<Types>;
};

const MenuItem = Object.assign(_MenuItem, {
  types: Object.keys(types).reduce(
    (obj, key) => ({ ...obj, [key]: key }),
    {}
  ) as RecordKeys<Types>,
});

MenuItem.displayName = "MenuItem";

export default MenuItem;
